import type { UploadFileStatus } from '../../../context/progressContext'
import type { UploadStatus, UploadTree } from './types'

let uniqueUploadId = 0

export const UPLOAD_CONSTANTS = {
	MAX_CONCURRENT_UPLOADS: 10,
	REFETCH_DELAY: 1000,
	PROGRESS_UPDATE_THRESHOLD: 5,
	TREE_BUILD_BATCH_SIZE: 250,
} as const

export const generateUploadId = (prefix = ''): string =>
	`${prefix}${Date.now()}-${uniqueUploadId++}`

export const isFileUploading = (
	statuses: UploadFileStatus[],
	fileName: string,
	folderPath = '',
) =>
	statuses.some(
		(status) =>
			status.nodeType === 'file' &&
			status.originalFileName === fileName &&
			(status.folderPath || '') === folderPath &&
			(status.status === 'pending' || status.status === 'uploading'),
	)

export const checkFilesAlreadyUploading = (
	statuses: UploadFileStatus[],
	files: File[],
	folderPath = '',
) => files.filter((file) => !isFileUploading(statuses, file.name, folderPath))

export const normalizeRelativePath = (path: string, fallback: string): string =>
	path.split('/').filter(Boolean).join('/') || fallback

export interface UploadTreeBuildProgress {
	processedFiles: number
	totalFiles: number
	rootItemsCount: number
	statuses: UploadFileStatus[]
}

interface BuildUploadTreeOptions {
	onProgress?: (progress: UploadTreeBuildProgress) => void
	isCanceled?: (statusId: string) => boolean
}

const yieldToBrowser = () =>
	new Promise<void>((resolve) => window.setTimeout(resolve, 0))

export const buildUploadTreeIncrementally = async (
	files: File[],
	relativePaths: string[],
	baseFolderId: string,
	{ onProgress, isCanceled }: BuildUploadTreeOptions = {},
): Promise<UploadTree> => {
	const statuses: UploadFileStatus[] = []
	const folderStatusIds = new Map<string, string>()
	const statusById = new Map<string, UploadFileStatus>()
	const rootStatusIds: string[] = []

	const addStatus = (status: UploadFileStatus) => {
		statuses.push(status)
		statusById.set(status.id!, status)
		if (status.parentStatusId) {
			const parent = statusById.get(status.parentStatusId)
			parent?.childrenIds?.push(status.id!)
		} else {
			rootStatusIds.push(status.id!)
		}
	}

	const getInitialStatus = (statusId: string, parentStatusId?: string) => {
		if (isCanceled?.(statusId)) return 'canceled' as const

		let currentParentId = parentStatusId
		while (currentParentId) {
			if (isCanceled?.(currentParentId)) return 'canceled' as const
			currentParentId = statusById.get(currentParentId)?.parentStatusId
		}

		return 'pending' as const
	}

	const incrementFolderFileCounts = (parentStatusId?: string) => {
		let currentParentId = parentStatusId
		while (currentParentId) {
			const parent = statusById.get(currentParentId)
			if (!parent) break
			parent.filesCount = (parent.filesCount || 0) + 1
			currentParentId = parent.parentStatusId
		}
	}

	const snapshotStatuses = () =>
		statuses.map((status) => {
			let currentStatus: UploadFileStatus | undefined = status
			let isInCanceledBranch = false

			while (currentStatus?.id) {
				if (isCanceled?.(currentStatus.id)) {
					isInCanceledBranch = true
					break
				}
				currentStatus = currentStatus.parentStatusId
					? statusById.get(currentStatus.parentStatusId)
					: undefined
			}

			return {
				...status,
				status:
					isInCanceledBranch && status.status === 'pending'
						? ('canceled' as const)
						: status.status,
				childrenIds: status.childrenIds ? [...status.childrenIds] : undefined,
			}
		})

	for (let index = 0; index < files.length; index += 1) {
		const file = files[index]
		const relativePath = normalizeRelativePath(
			relativePaths[index] || file.name,
			file.name,
		)
		const pathParts = relativePath.split('/')
		const fileName = pathParts[pathParts.length - 1]
		const folderParts = pathParts.slice(0, -1)
		let parentStatusId: string | undefined

		folderParts.forEach((folderName, folderIndex) => {
			const folderPath = folderParts.slice(0, folderIndex + 1).join('/')
			let folderStatusId = folderStatusIds.get(folderPath)

			if (!folderStatusId) {
				folderStatusId = generateUploadId('folder-')
				folderStatusIds.set(folderPath, folderStatusId)
				addStatus({
					id: folderStatusId,
					nodeType: 'folder',
					isFolder: true,
					folderName,
					originalFileName: folderName,
					status: getInitialStatus(folderStatusId, parentStatusId),
					progress: 0,
					storageKey: '',
					currentFolderId: baseFolderId,
					folderPath,
					relativePath: folderPath,
					parentStatusId,
					childrenIds: [],
					depth: folderIndex,
					filesCount: 0,
					uploadedFilesCount: 0,
				})
			}

			parentStatusId = folderStatusId
		})

		const fileStatusId = generateUploadId('file-')
		addStatus({
			id: fileStatusId,
			nodeType: 'file',
			file,
			originalFileName: fileName,
			status: getInitialStatus(fileStatusId, parentStatusId),
			progress: 0,
			storageKey: '',
			currentFolderId: baseFolderId,
			abortController: new AbortController(),
			folderPath: folderParts.join('/'),
			relativePath,
			parentStatusId,
			childrenIds: [],
			depth: folderParts.length,
		})
		incrementFolderFileCounts(parentStatusId)

		const processedFiles = index + 1
		if (
			processedFiles % UPLOAD_CONSTANTS.TREE_BUILD_BATCH_SIZE === 0 ||
			processedFiles === files.length
		) {
			onProgress?.({
				processedFiles,
				totalFiles: files.length,
				rootItemsCount: rootStatusIds.length,
				statuses: snapshotStatuses(),
			})
			await yieldToBrowser()
		}
	}

	return {
		rootStatusIds,
		statuses,
	}
}

export const getDescendantStatusIds = (
	statuses: UploadFileStatus[],
	statusId: string,
): string[] => {
	const statusById = new Map(statuses.map((status) => [status.id, status]))
	return getDescendantStatusIdsFromIndex(statusById, statusId)
}

const getDescendantStatusIdsFromIndex = (
	statusById: Map<string | undefined, UploadFileStatus>,
	statusId: string,
): string[] => {
	const descendantIds: string[] = []
	const pendingIds = [...(statusById.get(statusId)?.childrenIds || [])]

	while (pendingIds.length > 0) {
		const currentId = pendingIds.pop()
		if (!currentId) continue
		descendantIds.push(currentId)
		pendingIds.push(...(statusById.get(currentId)?.childrenIds || []))
	}

	return descendantIds
}

const getFolderStateFromIndex = (
	statusById: Map<string | undefined, UploadFileStatus>,
	folderStatusId: string,
): Pick<UploadFileStatus, 'status' | 'progress' | 'uploadedFilesCount'> => {
	const folder = statusById.get(folderStatusId)
	if (!folder) return { status: 'pending', progress: 0, uploadedFilesCount: 0 }

	const pendingIds = [...(folder.childrenIds || [])]
	let filesCount = 0
	let completedCount = 0
	let hasFailedFiles = false
	let hasActiveFiles = false
	let hasCanceledFiles = false
	let totalProgress = 0

	while (pendingIds.length > 0) {
		const statusId = pendingIds.pop()
		if (!statusId) continue
		const status = statusById.get(statusId)
		if (!status) continue

		if (status.nodeType === 'folder') {
			if (status.status === 'failed') hasFailedFiles = true
			pendingIds.push(...(status.childrenIds || []))
			continue
		}
		if (status.nodeType !== 'file') continue

		filesCount += 1
		totalProgress +=
			status.status === 'completed' ||
			status.status === 'failed' ||
			status.status === 'canceled'
				? 100
				: status.progress
		if (status.status === 'completed') completedCount += 1
		if (status.status === 'failed') hasFailedFiles = true
		if (status.status === 'pending' || status.status === 'uploading') {
			hasActiveFiles = true
		}
		if (status.status === 'canceled') hasCanceledFiles = true
	}

	if (filesCount === 0) {
		return {
			status: folder.createdOnServer ? 'completed' : folder.status,
			progress: folder.createdOnServer ? 100 : folder.progress,
			uploadedFilesCount: 0,
		}
	}

	const hasCompletedFiles = completedCount > 0
	const progress = Math.round(totalProgress / filesCount)
	const isFolderActive = folder.createdOnServer || folder.status === 'uploading'
	const hasFolderCreationFailure = folder.status === 'failed'

	return {
		status: hasActiveFiles
			? isFolderActive
				? 'uploading'
				: 'pending'
			: hasFailedFiles || hasFolderCreationFailure
				? 'failed'
				: hasCanceledFiles && !hasCompletedFiles
					? 'canceled'
					: 'completed',
		progress: hasActiveFiles ? progress : 100,
		uploadedFilesCount: completedCount,
	}
}

export const refreshUploadFolderStates = (
	statuses: UploadFileStatus[],
): UploadFileStatus[] => {
	const statusById = new Map(statuses.map((status) => [status.id, status]))
	const folders = statuses
		.filter((status) => status.nodeType === 'folder' && status.id)
		.sort((first, second) => (second.depth || 0) - (first.depth || 0))

	folders.forEach((folder) => {
		const folderState = getFolderStateFromIndex(statusById, folder.id!)
		statusById.set(folder.id!, { ...folder, ...folderState })
	})

	return statuses.map((status) =>
		status.id ? statusById.get(status.id) || status : status,
	)
}

export const updateUploadStatus = (
	statuses: UploadFileStatus[],
	statusId: string,
	updates: Partial<UploadFileStatus>,
	updateParents = true,
): UploadFileStatus[] => {
	const statusById = new Map(statuses.map((status) => [status.id, status]))
	const status = statusById.get(statusId)
	if (!status) return statuses

	statusById.set(statusId, { ...status, ...updates })
	if (!updateParents) {
		return statuses.map((item) =>
			item.id === statusId ? statusById.get(statusId)! : item,
		)
	}

	let parentStatusId = status.parentStatusId

	while (parentStatusId) {
		const parent = statusById.get(parentStatusId)
		if (!parent) break

		const folderState = getFolderStateFromIndex(statusById, parentStatusId)
		statusById.set(parentStatusId, { ...parent, ...folderState })
		parentStatusId = parent.parentStatusId
	}

	return statuses.map((item) =>
		item.id ? statusById.get(item.id) || item : item,
	)
}

export const updateFolderState = (
	statuses: UploadFileStatus[],
	folderStatusId: string,
): UploadFileStatus[] => {
	const statusById = new Map(statuses.map((status) => [status.id, status]))
	const folderState = getFolderStateFromIndex(statusById, folderStatusId)
	return updateUploadStatus(statuses, folderStatusId, folderState)
}

export const getUploadTreeRoots = (statuses: UploadFileStatus[]) =>
	statuses.filter((status) => !status.parentStatusId)

export const matchesUploadFilter = (
	status: UploadStatus,
	filter: 'all' | UploadStatus,
) => filter === 'all' || status === filter
